<template>
  <div class="filter__label">
    <chiplist
      :key="listKey"
      :data-items="activeFilters"
      @datachange="handleDataChange"
      :chip="'chipTemplate'"
      class="listWrapper"
    >
      <template #chipTemplate="{props, index}">
        <chip
          :class="[props.dataItem.isCoreFilter ? 'coreFilter' : 'noCoreFilter', 'chip']"
          :key="index"
          :data-item="props.dataItem"
          :value="props.value"
          :text="sanitizeHtml(props.text)"
          :removable="true"
          :remove-icon="'k-i-close'"
          :rounded="null"
          v-show="props.dataItem.isFilteredBy"
        />
      </template>
    </chiplist>
    <kbutton
      v-if="labels?.length > 0"
      :theme-color="'base'"
      @click="resetFilters"
    >
      <span
        class="subline"
      >
        {{ $tv('reset-filter', 'categories') }}
      </span>
    </kbutton>
  </div>
</template>

<script>
import {ChipList, Chip} from '@progress/kendo-vue-buttons'
import {Button} from '@progress/kendo-vue-buttons'
import {sanitizeHtml} from '~/assets/js/utils'
import {
  VALUE_TYPES
} from '~/assets/js/filters'
import {formatDimensions} from 'assets/js/utils/format_dimensions'

export default {
  name: 'AttributeFilterLabel',
  components: {
    'chiplist': ChipList,
    'chip': Chip,
    kbutton: Button
  },
  props: {
    filters: {
      type: Array || null,
      required: true,
      default() {
        return []
      }
    },
    localizedMeasure: {
      type: [String, Object],
      default() {
        return null
      }
    },
    inchText: {
      type: Boolean,
      default() {
        return false
      }
    }
  },
  data() {
    return {
      labels: [],
      listKey: 0
    }
  },
  watch: {
    filters: {
      handler() {
        this.labels = [],
        this.listKey++
      }
    }
  },
  computed: {
    activeFilters() {
      let unit = this.localizedMeasure
      this.filters.forEach((val, index) => {
        if (this.labels[index] !== val) {
          this.labels.splice(index, 0, val)
        }
        this.labels[index].isFilteredBy = val.isFilteredBy
        this.labels[index].value = val.shortName
        this.labels[index].isTextVisible = val.isDisabled ? false : this.isTextVisible(val)
        if (val.activeValues) {
          this.labels[index].text = this.getChipText(val, unit)
        }
      })

      this.$emit('activeFilters', this.labels.filter(f => f.isTextVisible)?.length)

      if (this.inchText) {
        let inchChip = {isFilteredBy: 'Inch', value: 'true', isTextVisible: true, text: this.$tv('useInchAssortmentOnly', 'product'), shortName: 'inch'}
        return this.labels.concat([inchChip])
      }
      return this.labels
    }
  },
  methods: {
    handleDataChange (event) {
      this.labels = event.value
      const inchIndex = event.value.findIndex(l => l.shortName === 'inch')
      if (this.inchText && inchIndex === -1) {
        this.handleInchDataChange()
      } else {
        this.$emit('resetInput', this.labels)
      }
    },
    handleInchDataChange () {
      this.$emit('resetInchInput')
    },
    getChipText(dataItem, unit) {
      let label = this.$tv(dataItem.shortName, 'attributesShort', 'shortNameNotAvailable')

      if (dataItem.shortName === 'pricingFlag') {
        label = this.$tv('pricingFlag', 'product')
        return label
      }
      if (label === 'shortNameNotAvailable') {
        label = dataItem.label
      }
      return label + ': ' + this.getText(dataItem, unit)
    },
    getText(dataItem, unit) {
      if (dataItem.type === VALUE_TYPES.STRING || dataItem.type === VALUE_TYPES.MULTI_STRING || dataItem.type === VALUE_TYPES.MULTI_INTEGER) {
        let translatedValues = dataItem.activeValues.map(v => this.$tv(v, 'attributes.' + dataItem.shortName + '-values', v))
        return translatedValues.join(', ')
      }
      else if (dataItem.type === VALUE_TYPES.DROPDOWN || dataItem.type === VALUE_TYPES.TEXT) {
        return dataItem.activeValues
      }
      else if (dataItem.type === VALUE_TYPES.INTEGER || dataItem.type === VALUE_TYPES.FLOAT || dataItem.type === VALUE_TYPES.MULTI_DOUBLE) {
        let fromVal = dataItem.activeValues.from
        let toleranceVal = dataItem.activeValues.tolerance
        const locale = useNuxtApp().$i18n?.locale?.value || 'en'
        if (fromVal) {
          if (dataItem.unitOfMeasure === 'mm' && unit === 'inch' ) {
            fromVal = (fromVal / 25.4)
          }
        }
        return (dataItem.activeValues.from !== null ? formatDimensions(fromVal, locale, dataItem.localizedUnitOfMeasure === 'inch') : '/') + ' ' + (dataItem.activeValues.tolerance !== null ? `  +/-${(fromVal * toleranceVal / 100).toFixed(this.localizedMeasure === 'inch' ? 3 : 2) ?? '/'} ${dataItem.localizedUnitOfMeasure === 'inch' ? this.$tv('inch', 'unitsOfMeasure') : 'mm'}` : '- /')
      }
      else if (dataItem.type === VALUE_TYPES.DATE) {
        return (dataItem.activeValues.start ? dataItem.activeValues.start : '/') + ' - ' + (dataItem.activeValues.end ? dataItem.activeValues.end : '/')
      } else if (dataItem.type === VALUE_TYPES.BOOLEAN) {
        return dataItem.activeValues
      }

    },
    resetFilters() {
      this.$emit('resetInput')
    },
    isTextVisible(dataItem) {
      if (dataItem.customQuery) {
        return false
      }
      if (dataItem.type === VALUE_TYPES.STRING) {
        return dataItem.values?.length > 0
      }
      else if (dataItem.type === VALUE_TYPES.DROPDOWN || dataItem.type === VALUE_TYPES.TEXT) {
        return dataItem?.values && Object.keys(dataItem?.values)?.length > 0
      }
      else if (dataItem.type === VALUE_TYPES.INTEGER || dataItem.type === VALUE_TYPES.FLOAT) {
        return !!(dataItem?.values.from || dataItem?.values.to)
      }
      else if (dataItem.type === VALUE_TYPES.DATE) {
        return !!(dataItem?.values.start || dataItem.values.end)
      }
    },
    sanitizeHtml(html) {
      return sanitizeHtml(html, window?.document)
    }
  }
}
</script>

<style lang="scss">
.filter__label {
  .k-chip-list {
    display: flex;
    flex-flow: row wrap;

    $gap: 1rem;

    margin: -1rem 0 0 -#{$gap};
    width: calc(100% + #{$gap});

    .chip {
      margin: 2.4375rem 0 0 #{$gap};
      background-color: $color__white;
      border: .0625rem solid $color--input__border__secondary;
      border-radius: .25rem;
      min-height: 2.25rem;
      width: 11.1875rem;
      font-weight: $base-text-font-weight-light;
      height: auto;
      padding: .125rem;

      .k-svg-i-close {
        font-size: 1.0625rem;

        svg {
          display: none;
        }
      }

      .k-chip-content {
        word-wrap: normal;
        overflow: initial;
        text-overflow: initial;
        white-space: normal;
        flex: 1 1 100%;

        .k-chip-label {
          white-space: break-spaces;
          display: contents;
        }
      }

      .k-chip-actions {
        margin-right: .5rem;
      }

      &.coreFilter {
        order: 1;
      }

      &.noCoreFilter {
        order: 2;
      }
    }

    @include breakpoint-down($md) {
      $gap: 1.75rem;

      margin: -1rem 0 0 -#{$gap};
      width: calc(100% + #{$gap});

      .chip {
        margin: $default-margin-padding-width 0 0 #{$gap};
        font-size: $base-text-font-size;
        width: calc(50% - #{$gap});
      }
    }
  }

  .k-button {
    margin-top: $default-margin-padding-2x;
  }
}
</style>
